"use client";

import { ChevronDown, ChevronUp, Clock, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ParticipantList } from "./base-match-template";

// Re-export shared types
export {
  type Participant,
  suggestionToParticipant,
} from "./base-match-template";

// ──────────────────────────────────────────────────────────────────────────────
// DURATION PICKER
// ──────────────────────────────────────────────────────────────────────────────

type DurationPickerProps = {
  label?: string;
  value: number; // in minutes
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  presets?: number[];
};

export function DurationPicker({
  label = "Duration",
  value,
  onChange,
  min = 5,
  max = 480,
  step = 5,
  presets = [15, 30, 45, 60, 90, 120],
}: DurationPickerProps) {
  const formatDuration = (mins: number) => {
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remaining = mins % 60;
    return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
  };

  return (
    <div className="space-y-3">
      <Label className="text-text-primary">{label}</Label>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <Button
            key={preset}
            type="button"
            variant={value === preset ? "default" : "outline"}
            size="sm"
            onClick={() => onChange(preset)}
            className={cn(
              "h-8",
              value === preset && "bg-brand text-text-inverse",
            )}
          >
            {formatDuration(preset)}
          </Button>
        ))}
      </div>

      {/* Custom input */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9"
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={value <= min}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
          <Clock className="h-4 w-4 text-text-secondary" />
          <span className="font-medium text-text-primary">
            {formatDuration(value)}
          </span>
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9"
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={value >= max}
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// INTENSITY SELECTOR
// ──────────────────────────────────────────────────────────────────────────────

export type IntensityLevel = "light" | "moderate" | "intense";

type IntensitySelectorProps = {
  label?: string;
  value: IntensityLevel;
  onChange: (value: IntensityLevel) => void;
};

const intensityConfig: Record<
  IntensityLevel,
  { label: string; color: string; flames: number }
> = {
  light: { label: "Light", color: "text-green-500", flames: 1 },
  moderate: { label: "Moderate", color: "text-orange-500", flames: 2 },
  intense: { label: "Intense", color: "text-red-500", flames: 3 },
};

export function IntensitySelector({
  label = "Intensity",
  value,
  onChange,
}: IntensitySelectorProps) {
  return (
    <div className="space-y-2">
      <Label className="text-text-primary">{label}</Label>
      <div className="flex gap-2">
        {(Object.keys(intensityConfig) as IntensityLevel[]).map((level) => {
          const config = intensityConfig[level];
          const isSelected = value === level;

          return (
            <Button
              key={level}
              type="button"
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => onChange(level)}
              className={cn(
                "flex-1 gap-1",
                isSelected && "bg-brand text-text-inverse",
              )}
            >
              <div className="flex">
                {Array.from({ length: config.flames }).map((_, i) => (
                  <Flame
                    // biome-ignore lint/suspicious/noArrayIndexKey: flames are positional and never reorder
                    key={`flame-${level}-${i}`}
                    className={cn(
                      "-mr-1 h-3.5 w-3.5",
                      isSelected ? "text-text-inverse" : config.color,
                    )}
                  />
                ))}
              </div>
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// STYLE/TYPE SELECTOR (Radio-like pills)
// ──────────────────────────────────────────────────────────────────────────────

type StyleOption = {
  value: string;
  label: string;
  icon?: React.ReactNode;
};

type StyleSelectorProps = {
  label: string;
  options: StyleOption[];
  value: string;
  onChange: (value: string) => void;
  columns?: 2 | 3 | 4;
};

export function StyleSelector({
  label,
  options,
  value,
  onChange,
  columns = 3,
}: StyleSelectorProps) {
  const gridClass = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  }[columns];

  return (
    <div className="space-y-2">
      <Label className="text-text-primary">{label}</Label>
      <div className={cn("grid gap-2", gridClass)}>
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                "flex items-center justify-center gap-1.5 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors",
                isSelected
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-border bg-surface hover:border-brand/50",
              )}
            >
              {option.icon}
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISTANCE INPUT
// ──────────────────────────────────────────────────────────────────────────────

type DistanceInputProps = {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  unit?: "km" | "m" | "miles";
  step?: number;
};

export function DistanceInput({
  label = "Distance",
  value,
  onChange,
  unit = "km",
  step = 0.1,
}: DistanceInputProps) {
  return (
    <div className="space-y-2">
      <Label className="text-text-primary">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value || ""}
          onChange={(e) => onChange(Number(e.target.value))}
          step={step}
          min={0}
          className="w-24"
          placeholder="0"
        />
        <span className="text-sm text-text-secondary">{unit}</span>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// TIME INPUT (HH:MM:SS or MM:SS)
// ──────────────────────────────────────────────────────────────────────────────

type TimeInputProps = {
  label?: string;
  value: { hours?: number; minutes: number; seconds: number };
  onChange: (value: {
    hours?: number;
    minutes: number;
    seconds: number;
  }) => void;
  showHours?: boolean;
};

export function TimeInput({
  label = "Time",
  value,
  onChange,
  showHours = false,
}: TimeInputProps) {
  return (
    <div className="space-y-2">
      <Label className="text-text-primary">{label}</Label>
      <div className="flex items-center gap-1">
        {showHours && (
          <>
            <Input
              type="number"
              value={value.hours || ""}
              onChange={(e) =>
                onChange({ ...value, hours: Number(e.target.value) })
              }
              min={0}
              max={99}
              className="w-14 text-center"
              placeholder="0"
            />
            <span className="text-text-secondary">:</span>
          </>
        )}
        <Input
          type="number"
          value={value.minutes || ""}
          onChange={(e) =>
            onChange({ ...value, minutes: Number(e.target.value) })
          }
          min={0}
          max={59}
          className="w-14 text-center"
          placeholder="00"
        />
        <span className="text-text-secondary">:</span>
        <Input
          type="number"
          value={value.seconds || ""}
          onChange={(e) =>
            onChange({ ...value, seconds: Number(e.target.value) })
          }
          min={0}
          max={59}
          className="w-14 text-center"
          placeholder="00"
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// LOCATION INPUT
// ──────────────────────────────────────────────────────────────────────────────

type LocationInputProps = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export function LocationInput({
  label = "Location",
  value,
  onChange,
  placeholder = "Enter location...",
}: LocationInputProps) {
  return (
    <div className="space-y-2">
      <Label className="text-text-primary">{label}</Label>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full"
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// SESSION BUDDIES (Simplified participant picker)
// ──────────────────────────────────────────────────────────────────────────────

export { ParticipantList };
